"use client";
import React, { ReactNode, useState } from "react";
import { Spinner } from "@/components/spinner";

interface Props {
  /**
   * Button style
   */
  style?: "primary" | "secondary" | "tertiary";
  customizedStyle?: {
    color?: string;
    background?: string;
    hover: {
      background?: string;
    };
  };
  /**
   * Button Size
   */
  size?: "xs" | "sm" | "base" | "lg" | "xl";
  /**
   * Optional, button label. Note will be ignored when children are passed
   */
  label?: string;
  /**
   * Overrides all styling of the button
   */
  className?: string;
  /**
   * classes for disabled style
   */
  disabledClassName?: string;
  /**
   * Disabled state
   */
  isDisabled?: boolean;
  /**
   * Loading message
   */
  loadingMessage?: string;
  /**
   * Loading state
   */
  isLoading?: boolean;
  /**
   * Children
   */
  children?: ReactNode;
  /**
   * Optional click handler
   */
  callback?: () => void;
  /**
   * id
   */
  id?: string;
  /**
   * appends class to the defined classes by the style or className prop
   */
  appendClass?: string;
}

/**
 * Button component
 */
export const Button: React.FC<Props> = ({
  style = "primary",
  customizedStyle,
  size = "base",
  id = "",
  isDisabled = false,
  isLoading = false,
  loadingMessage = "",
  label,
  className,
  disabledClassName,
  callback,
  children,
  appendClass = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  let buttonStyle = "";

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (className) {
    buttonStyle = className;
  } else if (style === "primary") {
    buttonStyle = `w-full md:w-fit text-white bg-primary ${!isLoading && "hover:bg-dark-primary focus:ring-2 focus:ring-blue-300"} font-medium rounded-lg focus:outline-none`;
  } else if (style === "secondary") {
    buttonStyle = `w-full md:w-fit font-medium text-gray-900 focus:outline-none bg-transparent rounded-lg border border-1 border-gray-300 ${!isLoading && "hover:bg-gray-100 hover:bg-opacity-20 hover:stroke-blue-700 hover:text-primary focus:z-10 focus:ring-2 focus:ring-primary-500"}`;
  } else if (style === "tertiary") {
    buttonStyle = `w-full md:w-fit font-normal text-primary focus:ring-2 focus:ring-blue-300 ${!isLoading && !isDisabled && "hover:cursor-pointer hover:underline hover:decoration-primary focus:z-10"}`;
  }

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isDisabled || isLoading || !callback) return;

    e.stopPropagation();
    callback();
  };

  const spinnerClass =
    style === "secondary"
      ? "fill-primary text-gray-200"
      : "fill-gray-200 text-primary";

  let sizeClass = "";

  switch (size) {
    case "xs":
      sizeClass = "px-3 py-2 text-xs";
      break;
    case "sm":
      sizeClass = "px-3 py-2 text-sm";
      break;
    case "base":
      sizeClass = "px-5 h-10 text-sm flex items-center justify-center";
      break;
    case "lg":
      sizeClass = "px-5 py-3 text-base";
      break;
    case "xl":
      sizeClass = "px-6 py-3.5 text-base";
      break;
  }

  return (
    <button
      id={id}
      disabled={isDisabled}
      type="button"
      onClick={onClick}
      className={`inline-flex justify-center rounded-lg font-medium disabled:cursor-not-allowed ${buttonStyle} ${style !== "tertiary" ? sizeClass : ""} ${
        style === "tertiary"
          ? "rounded-none disabled:text-gray-400 disabled:no-underline"
          : disabledClassName ||
            "disabled:border-0 disabled:bg-gray-400 disabled:text-white disabled:no-underline"
      } ${isLoading ? "cursor-wait" : ""} ${appendClass}`}
      {...(customizedStyle && style === "primary"
        ? {
            style: {
              color: customizedStyle.color,
              background: isHovered
                ? customizedStyle?.hover.background
                : customizedStyle?.background,
            },
          }
        : {})}
      {...(customizedStyle && (style === "secondary" || style === "tertiary")
        ? {
            style: {
              color: customizedStyle.color,
              ...(style === "secondary" && {
                borderColor: customizedStyle.color,
              }),
            },
          }
        : {})}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
    >
      {!isDisabled && isLoading ? (
        <div className="flex items-center justify-center">
          <div className="h-4 w-4">
            <Spinner className={spinnerClass} />
          </div>
          <span className="ml-2">{loadingMessage}</span>
        </div>
      ) : (
        <>{children || label}</>
      )}
    </button>
  );
};
